import { useEffect, useRef } from 'react';

const CLOSE_BUTTON_TESTID = 'inbox-header-close-button';

// Mobile z-index sits below mini games (3) when closed, above site header (4) when open.
const Z = {
    desktop: '11',
    mobileOpen: '6',
    mobileClosed: '1',
} as const;

// Mobile bottom offset. With chat launchers the badge is stacked clear of the launcher.
const MOBILE_BOTTOM = {
    sports: {
        withIntercom: '161px',
        withFreshChat: '145px',
        withoutChat: '115px',
    },
    casino: {
        withIntercom: '126px',
        withFreshChat: '105px',
        withoutChat: '70px',
    },
} as const;

const DESKTOP_RIGHT = {
    withIntercom: '85px',
    withFreshChat: '145px',
    withoutChat: '25px',
} as const;

interface HostPosition {
    zIndex: string;
    bottom: string;
    right: string;
}

const getHostPosition = (
    isDesktop: boolean,
    isCasino: boolean,
    hasIntercom: boolean,
    hasFreshChat: boolean,
    isOpen: boolean
): HostPosition => {
    if (isDesktop) {
        return {
            zIndex: Z.desktop,
            bottom: '28px',
            right: hasIntercom
                ? DESKTOP_RIGHT.withIntercom
                : hasFreshChat
                  ? DESKTOP_RIGHT.withFreshChat
                  : DESKTOP_RIGHT.withoutChat,
        };
    }

    const layout = isCasino ? MOBILE_BOTTOM.casino : MOBILE_BOTTOM.sports;
    return {
        zIndex: isOpen ? Z.mobileOpen : Z.mobileClosed,
        bottom: hasIntercom ? layout.withIntercom : hasFreshChat ? layout.withFreshChat : layout.withoutChat,
        right: hasIntercom ? '32px' : '25px',
    };
};

/**
 * Positions the Optimove badge host element and manages z-index when the inbox opens.
 *
 * The SDK uses an open shadow DOM, so composedPath() lets us distinguish badge clicks (open)
 * from close button clicks. On mobile the host sits below mini games (z-index 1) until the
 * inbox opens, then rises above the site header (z-index 6). On desktop z-index 11 always.
 *
 * Two effects are intentional: the click handler must not re-run on breakpoint/casino changes
 * or it would reset open state mid-session.
 */
export const useOptimoveHost = (
    id: string | null,
    isDesktop: boolean,
    isCasino: boolean,
    hasIntercom: boolean,
    hasFreshChat: boolean
): void => {
    // Refs let the stable click-handler effect read current values without being in its deps.
    const isDesktopRef = useRef(isDesktop);
    isDesktopRef.current = isDesktop;

    const isOpenRef = useRef(false);

    // Re-runs on layout changes to update position. Skips z-index reset if inbox is open.
    useEffect(() => {
        if (id === null) return;
        const host = document.getElementById(id);
        if (host === null) return;

        host.style.position = 'fixed';
        host.style.left = 'auto';
        host.style.top = 'auto';
        host.style.overflow = 'visible';
        host.style.width = '48px';
        host.style.height = '48px';

        const { zIndex, bottom, right } = getHostPosition(
            isDesktop,
            isCasino,
            hasIntercom,
            hasFreshChat,
            isOpenRef.current
        );
        host.style.zIndex = zIndex;
        host.style.bottom = bottom;
        host.style.right = right;
    }, [isDesktop, isCasino, hasIntercom, hasFreshChat, id]);

    // Stable — only re-runs if the host element itself changes.
    useEffect(() => {
        if (id === null) return;
        const host = document.getElementById(id);
        if (host === null) return;

        // Shadow DOM is open — composedPath() includes shadow elements, so we can detect
        // the close button click directly rather than inferring state from outside clicks.
        const handleHostClick = (e: MouseEvent): void => {
            const isCloseButton = e
                .composedPath()
                .some((el) => el instanceof Element && el.getAttribute('data-testid') === CLOSE_BUTTON_TESTID);
            if (isCloseButton) {
                isOpenRef.current = false;
                host.removeAttribute('data-open');
                if (!isDesktopRef.current) host.style.zIndex = Z.mobileClosed;
            } else if (!isOpenRef.current) {
                isOpenRef.current = true;
                host.setAttribute('data-open', '');
                if (!isDesktopRef.current) host.style.zIndex = Z.mobileOpen;
            }
        };

        host.addEventListener('click', handleHostClick);

        const shadow = host.querySelector('#optimobile-inbox-ui-root-inbox-container')?.shadowRoot ?? null;
        let overscrollStyle: HTMLStyleElement | null = null;
        if (shadow !== null) {
            overscrollStyle = document.createElement('style');
            overscrollStyle.textContent = '.optimobile-message-inbox { overscroll-behavior: contain; }';
            shadow.appendChild(overscrollStyle);
        }

        return () => {
            host.removeEventListener('click', handleHostClick);
            overscrollStyle?.remove();
        };
    }, [id]);
};
